import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/sections.css";
import "./styles/transitions.css";
import { createApp } from "vue";
import App from "./App.vue";
import { router } from "./router";
import { initReveal } from "./ui/reveal";
import { initContactForm } from "./ui/contact-form";
import { runBootGate, fontsSettled } from "./ui/boot";
import { cssDurationMs } from "./ui/css-time";
import { detectCapabilities, prefersReducedMotion } from "./scene/capabilities";
import type { SceneHandle } from "./scene";
import type { initScrollRig } from "./scene/scroll-rig";

document.documentElement.classList.add("js");
createApp(App).use(router).mount("#app");

// --------------------------------------------------------------------- //
// The mountain                                                          //
// --------------------------------------------------------------------- //
// One scene for the whole session. It is booted once, below, and from then
// on route changes only tell it whether the climb is on screen - the canvas,
// the terrain and the trail are never rebuilt, which is the entire reason
// the router exists rather than letting each page load its own copy.

let scene: SceneHandle | null = null;
let mountClimb: typeof initScrollRig | null = null;
let unmountClimb: (() => void) | null = null;

/**
 * Brings the mountain into line with whichever route is now mounted.
 *
 * Called from two directions - a route change, and the scene finishing its
 * (asynchronous) boot - because either can be the one that happens last.
 * Doing nothing until both have happened is what makes a click during the
 * boot gate, or a deep link straight onto /services, land in the same
 * state as the ordinary path through the site.
 */
function syncScene(): void {
  if (!scene || !mountClimb) return;

  unmountClimb?.();
  unmountClimb = null;

  if (window.location.pathname !== "/") {
    scene.setClimbing(false, cssDurationMs("--route-scene-return", 900));
    return;
  }

  scene.setClimbing(true);
  unmountClimb = mountClimb(scene.trail, scene.cameraRig);
}

/**
 * Fetches and builds the scene, resolving once it has a frame on screen.
 *
 * No longer deferred to requestIdleCallback. That was right when the page
 * behind it was already visible and the scene was competing with it for the
 * main thread - but the page is now held behind the boot gate until this
 * resolves, so every millisecond spent waiting for idle is a millisecond of
 * blank ground the visitor is looking at.
 *
 * Resolves rather than rejects on every failure path. The gate is waiting on
 * this promise and a visitor whose GPU cannot run the scene still has a site
 * to read.
 */
async function bootScene(): Promise<void> {
  if (prefersReducedMotion()) return;

  const capabilities = detectCapabilities();
  // Cheap check, no three.js involved yet - skip the download entirely
  // rather than fetch a chunk that can only ever return null.
  if (!capabilities.webgl2) return;

  try {
    const [{ initScene }, { initScrollRig: mount }] = await Promise.all([
      import("./scene"),
      import("./scene/scroll-rig"),
    ]);

    const handle = initScene(capabilities);
    if (!handle) return;

    scene = handle;
    mountClimb = mount;
    // The photographic stand-in has done its job for this session - and with
    // the canvas persisting across routes, there is no later page load to
    // put it back.
    document.querySelector(".scene-fallback")?.remove();

    syncScene();
    await handle.firstFrame;
  } catch {
    // A failed chunk fetch leaves the CSS fallback in place, which is the
    // same picture a no-WebGL2 visitor gets.
  }
}

// The gate comes down when the scene is on screen AND the webfonts have
// settled - the two things that would otherwise visibly change under the
// visitor a moment after they started reading.
runBootGate(Promise.all([bootScene(), fontsSettled()]));

initReveal();
initContactForm();
window.addEventListener("app:route-change", syncScene);
